<script lang="ts" setup>
import CheckOutlineIcon from "@/components/icon/CheckOutlineIcon.vue";
import XmarkCircleIcon from "@/components/icon/XmarkCircleIcon.vue";
import EmptyContent from "@/components/mobile/common/EmptyContent.vue";
import ThreadItem from "@/components/mobile/ThreadItem.vue";
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import i18n from "@/locales";
import { getDisplayDate } from "@/modules/common";
import { onCurrentMateChange } from "@/stores/internal-subscribe";
import { useMateCommit } from "@/stores/mateCommit";
import { Commit } from "@/types/entity";
import { showConfirmDialog, showSuccessToast, Tab, Tabs } from "vant";
import { onBeforeMount, ref, watch } from "vue";
import { useRouter } from "vue-router";

// 内部状态
const router = useRouter();
const commitStore = useMateCommit();
const currentThreadTab = ref<0 | 1>(0);
const isLoading = ref(false);
// Lifecycle
const prepareData = async () => {
  isLoading.value = true;
  if (currentThreadTab.value === 0) {
    await commitStore.getPendingList();
  } else if (currentThreadTab.value === 1) {
    await commitStore.getFinishedList();
  } else if (currentThreadTab.value === 2) {
    await commitStore.getPendingCommitList();
  } else if (currentThreadTab.value === 3) {
    await commitStore.getFinishedCommitList();
  }
  isLoading.value = false;
};

onBeforeMount(async () => {
  await prepareData();
});
onCurrentMateChange(async () => {
  await prepareData();
});
watch(currentThreadTab, async () => {
  await prepareData();
});

// Event handlers
const showApproveConfirm = async (commit: Commit) => {
  const isAdmirer = !!commit.sender;
  try {
    if (!isAdmirer)
      await showConfirmDialog({
        title: i18n.global.t("ConfirmModal.confirmApptovalTitle"),
        message: i18n.global.t("ConfirmModal.confirmApptovalInfo"),
        confirmButtonText: i18n.global.t("Buttons.confirm"),
        cancelButtonText: i18n.global.t("Buttons.cancel"),
      });
    await commitStore.approve(commit.id, isAdmirer);
    showSuccessToast(i18n.global.t("Message.approved"));
  } catch {
    // click cancel: do nothing
    return;
  }
};

const onCancel = async (commit: Commit) => {
  const isAdmirer = !!commit.sender;
  try {
    if (!isAdmirer)
      await showConfirmDialog({
        title: i18n.global.t("ConfirmModal.confirmRejectionTitle"),
        message: i18n.global.t("ConfirmModal.confirmRejectionInfo"),
        confirmButtonText: i18n.global.t("Buttons.confirm"),
        cancelButtonText: i18n.global.t("Buttons.cancel"),
      });
    await commitStore.reject(commit.id, isAdmirer);
    showSuccessToast(i18n.global.t("Message.rejected"));
  } catch {
    // click cancel: do nothing
    return;
  }
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr]">
    <!-- 顶部导航 -->
    <threads-nav :show-back="true" :show-center="false" />
    <!-- 列表 -->
    <div class="w-full overflow-hidden relative">
      <tabs
        v-model:active="currentThreadTab"
        swipeable
        class="h-full thread-tabs grid grid-rows-[auto_1fr]"
      >
        <tab
          :title="i18n.global.t('Request.pendingReview')"
          class="overflow-y-auto"
        >
          <div class="p-2" v-if="commitStore.pending.list.length > 0">
            <div
              v-for="item in commitStore.pending.list"
              :key="item.id"
              class="border-b py-2"
            >
              <template v-if="!item.receiver">
                <thread-item
                  class="border-b-0"
                  :uuid="item.id.toString()"
                  :mate-id="item.mate.uuid"
                  :avatar-url="item.mate.mediaUrl"
                  :title="item.mate.name"
                  :time="item.dtUpdate"
                  :type="item.mate.type"
                  :content="item.title"
                  :show-comment="false"
                  :enable-reaction-btn="false"
                />
                <div class="pl-10">
                  <thread-item
                    class="border-b-0"
                    :uuid="item.moment.uuid"
                    :mate-id="item.moment.mate.uuid"
                    :avatar-url="item.moment.mate.mediaUrl"
                    :title="item.moment.mate.name"
                    :content="item.moment.text"
                    :mention-matezz="item.moment.mentionMatezz ?? []"
                    :media-list="item.moment.mediazz"
                    :chat-message-list="item.moment.chatMessagezz"
                    :show-comment="false"
                    :enable-reaction-btn="false"
                    @thread-click="
                      router.push({
                        name: 'threadDetail',
                        params: { uuid: item.moment.uuid },
                        query: { from: 'requestList' },
                      })
                    "
                  />
                </div>
              </template>
              <div v-else>
                <div class="flex items-center gap-4 mb-4">
                  <UserAvatar
                    :avatar-url="item.sender.mediaUrl"
                    class="w-10"
                    @click="
                      $router.push({
                        name: 'mateHome',
                        params: { uuid: item.sender.uuid },
                      })
                    "
                  />
                  <p>
                    {{ item.sender.name }} {{ $t("Request.applyGuardYou") }}
                  </p>
                </div>
              </div>
              <div class="flex justify-center gap-2">
                <button
                  class="btn btn-sm rounded btn-primary"
                  @click="showApproveConfirm(item)"
                >
                  <check-outline-icon class="icon-base" />
                  {{ $t("Buttons.agree") }}
                </button>
                <button class="btn btn-sm rounded" @click="onCancel(item)">
                  <xmark-circle-icon class="w-4" />
                  {{ $t("Buttons.deny") }}
                </button>
              </div>
            </div>
            <div class="py-5 text-center text-base-content/50">
              {{ $t("Message.allNow") }}
            </div>
          </div>
          <div
            v-else-if="isLoading"
            class="flex flex-col items-center justify-center h-[200px]"
          >
            <div class="flex flex-col items-center justify-center">
              <span class="loading" />
            </div>
          </div>
          <EmptyContent
            v-else
            :title="i18n.global.t('Message.noContentAvailable')"
          />
        </tab>
        <tab :title="i18n.global.t('Request.reviewed')" class="overflow-y-auto">
          <div class="p-2" v-if="commitStore.finished.list.length > 0">
            <div
              v-for="item in commitStore.finished.list"
              :key="item.id"
              class="border-b py-2"
            >
              <template v-if="!item.receiver">
                <thread-item
                  class="border-b-0"
                  :mate-id="item.mate.uuid"
                  :avatar-url="item.mate.mediaUrl"
                  :title="item.mate.name"
                  :time="item.dtUpdate"
                  :type="item.mate.type"
                  :content="item.title"
                  :show-comment="false"
                  :enable-reaction-btn="false"
                />
                <div class="pl-10">
                  <thread-item
                    class="border-b-0"
                    :mate-id="item.moment.mate.uuid"
                    :avatar-url="item.moment.mate.mediaUrl"
                    :title="item.moment.mate.name"
                    :content="item.moment.text"
                    :mention-matezz="item.moment.mentionMatezz ?? []"
                    :media-list="item.moment.mediazz"
                    :chat-message-list="item.moment.chatMessagezz"
                    :show-comment="false"
                    :enable-reaction-btn="false"
                    @thread-click="
                      router.push({
                        name: 'threadDetail',
                        params: { uuid: item.moment.uuid },
                      })
                    "
                  />
                </div>
              </template>
              <div v-else>
                <div class="flex items-center gap-4 mb-4">
                  <UserAvatar
                    :avatar-url="item.sender.mediaUrl"
                    class="w-10"
                    @click="
                      $router.push({
                        name: 'mateHome',
                        params: { uuid: item.sender.uuid },
                      })
                    "
                  />
                  <p>
                    {{ item.sender.name }} {{ $t("Request.applyGuardYou") }}
                  </p>
                </div>
              </div>
              <div class="flex justify-between gap-2">
                <div class="text-base-content/50">
                  {{ $t("Request.reviewTime") }}：{{
                    getDisplayDate(item.dtUpdate)
                  }}
                </div>
                <div
                  class="badge"
                  :class="{
                    'badge-success': item.status === 2,
                    'badge-error': item.status === 3,
                  }"
                >
                  {{
                    item.status === 2
                      ? $t("Request.status.approved")
                      : $t("Request.status.rejected")
                  }}
                </div>
              </div>
            </div>
          </div>
          <EmptyContent
            v-else
            :title="i18n.global.t('Message.noContentAvailable')"
          />
        </tab>
        <tab
          :title="i18n.global.t('Request.pendingTaReview')"
          class="overflow-y-auto"
        >
          <div class="p-2" v-if="commitStore.pendingCommit.list.length > 0">
            <div
              v-for="item in commitStore.pendingCommit.list"
              :key="item.id"
              class="border-b py-2"
            >
              <template v-if="!item.receiver">
                <thread-item
                  class="border-b-0"
                  :uuid="item.moment.uuid"
                  :mate-id="item.moment.mate.uuid"
                  :avatar-url="item.moment.mate.mediaUrl"
                  :title="item.moment.mate.name"
                  :content="item.moment.text"
                  :mention-matezz="item.moment.mentionMatezz ?? []"
                  :media-list="item.moment.mediazz"
                  :chat-message-list="item.moment.chatMessagezz"
                  :show-comment="false"
                  :enable-reaction-btn="false"
                  :from-mate="item.mate"
                  :time="item.dtCreate"
                  @thread-click="
                    router.push({
                      name: 'threadDetail',
                      params: { uuid: item.moment.uuid },
                      query: { from: 'requestList' },
                    })
                  "
                />
              </template>
              <div v-else>
                <div class="flex items-center gap-4 mb-4">
                  <UserAvatar
                    :avatar-url="item.receiver.mediaUrl"
                    class="w-10"
                  />
                  <p>{{ $t("Request.applyGuard") }} {{ item.receiver.name }}</p>
                </div>
              </div>
            </div>
            <div class="py-5 text-center text-base-content/50">
              {{ $t("Message.allNow") }}
            </div>
          </div>
          <div
            v-else-if="isLoading"
            class="flex flex-col items-center justify-center h-[200px]"
          >
            <div class="flex flex-col items-center justify-center">
              <span class="loading" />
            </div>
          </div>
          <EmptyContent
            v-else
            :title="i18n.global.t('Message.noContentAvailable')"
          />
        </tab>
        <tab
          :title="i18n.global.t('Request.taReviewed')"
          class="overflow-y-auto"
        >
          <div class="p-2" v-if="commitStore.finishedCommit.list.length > 0">
            <div
              class="border-b py-2"
              v-for="item in commitStore.finishedCommit.list"
              :key="item.id"
            >
              <template v-if="!item.receiver">
                <thread-item
                  class="border-b-0"
                  :mate-id="item.moment.mate.uuid"
                  :avatar-url="item.moment.mate.mediaUrl"
                  :title="item.moment.mate.name"
                  :content="item.moment.text"
                  :mention-matezz="item.moment.mentionMatezz ?? []"
                  :media-list="item.moment.mediazz"
                  :chat-message-list="item.moment.chatMessagezz"
                  :show-comment="false"
                  :enable-reaction-btn="false"
                  :from-mate="item.mate"
                  :time="item.dtCreate"
                  @thread-click="
                    router.push({
                      name: 'threadDetail',
                      params: { uuid: item.moment.uuid },
                    })
                  "
                />
              </template>
              <div v-else>
                <div class="flex items-center gap-4 mb-4">
                  <UserAvatar
                    :avatar-url="item.receiver.mediaUrl"
                    class="w-10"
                  />
                  <p>{{ $t("Request.applyGuard") }} {{ item.receiver.name }}</p>
                </div>
              </div>
              <div class="flex justify-between gap-2">
                <div class="text-base-content/50">
                  {{ $t("Request.reviewTime") }}：{{
                    getDisplayDate(item.dtUpdate)
                  }}
                </div>
                <div
                  class="badge"
                  :class="{
                    'badge-success': item.status === 2,
                    'badge-error': item.status === 3,
                  }"
                >
                  {{
                    item.status === 2
                      ? $t("Request.status.approved")
                      : $t("Request.status.rejected")
                  }}
                </div>
              </div>
            </div>
          </div>
          <EmptyContent
            v-else
            :title="i18n.global.t('Message.noContentAvailable')"
          />
        </tab>
      </tabs>
    </div>
  </div>
</template>
